import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { NicknameField } from '../../components/integrations/NicknameField';
import { resolveNickname } from '../../lib/mappingNickname';
import { TextField, TextArea } from '../../components/ui/TextField';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { fetchTemplate, searchTemplates } from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import { uid, type Mapping, type Template } from '../../types';
import { SYSTEM_COLLECTORS, SCRIPT_NAME, buildSystemSpec, hostMetricSignals, parseList, type CustomScript } from '../../lib/systemMetrics';
import { buildSignalTemplateEntries, COLORS, newSignalNames } from '../../lib/signalTemplate';
import { useChannelTemplates } from '../../lib/useChannelTemplates';

// Host metrics via the cue-cli agent: CPU, RAM, df, du, temperature, iostat,
// network, load, uptime. Nothing to fetch and nothing to authenticate - the
// agent reads its own machine - so setup is just what to collect, how often,
// and which channel gets the Signals.
export function SystemSetupScreen() {
  const navigate = useNavigate();
  const { channels, isChannelOwner, addMapping, addTemplate, updateTemplate, updateChannel, profile } = useStore();
  const ownedChannels = channels.filter(isChannelOwner);

  const [title, setTitle] = useState('');
  const [nicknameInput, setNicknameInput] = useState('');
  const [channelNickname, setChannelNickname] = useState(ownedChannels[0]?.nickname ?? '');
  const [picked, setPicked] = useState<Set<string>>(() => new Set(SYSTEM_COLLECTORS.filter((c) => c.defaultOn).map((c) => c.id)));
  const [intervalSec, setIntervalSec] = useState('60');
  const [duPaths, setDuPaths] = useState('');
  const [dfMounts, setDfMounts] = useState('');
  const [scripts, setScripts] = useState<CustomScript[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<Mapping | null>(null);

  // A fresh Host metrics integration has no `rules`/`transform` in its spec
  // (buildSystemSpec never writes any), so the agent sends every sample
  // straight through under its own fixed collector-given name (see
  // systemMetrics.ts's HOST_METRIC_SIGNALS header comment) - it will never
  // match an existing template by name unless one happens to already use
  // those exact names. Offer to generate one that does, right after create,
  // same as the auto-mapping wizard offers for other sources.
  const destChannel = channels.find((c) => c.nickname === channelNickname);
  const destTemplates = useChannelTemplates(destChannel);
  // Every template you own, not only ones already attached to this channel -
  // a host metric like `cpu_usage_percent` may already exist as a Signal
  // somewhere else you set up before; that template just needs attaching,
  // not a duplicate Signal minted under a "new" template.
  const [ownedTemplates, setOwnedTemplates] = useState<Template[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await searchTemplates('mine', '');
        const hydrated = (await Promise.all(page.data.map((n) => fetchTemplate(n)))).filter((t): t is Template => t !== null);
        if (!cancelled) setOwnedTemplates(hydrated);
      } catch {
        if (!cancelled) setOwnedTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const allTemplates = useMemo(() => {
    const byNickname = new Map(ownedTemplates.map((t) => [t.nickname, t]));
    for (const t of destTemplates) byNickname.set(t.nickname, t);
    return [...byNickname.values()];
  }, [ownedTemplates, destTemplates]);
  const existingSignalNames = useMemo(() => allTemplates.flatMap((t) => t.signals.map((s) => s.name)), [allTemplates]);
  // name (lowercased) -> the template it lives in, for auto-attaching a
  // template a produced host metric already matches but this channel
  // doesn't reference yet.
  const templateForSignalName = useMemo(() => {
    const out: Record<string, string> = {};
    for (const t of allTemplates) for (const sg of t.signals) if (!(sg.name.toLowerCase() in out)) out[sg.name.toLowerCase()] = t.nickname;
    return out;
  }, [allTemplates]);
  const producedSignals = useMemo(() => hostMetricSignals([...picked]), [picked]);
  const newHostSignals = useMemo(() => {
    const isNew = new Set(newSignalNames(producedSignals.map((s) => s.name), existingSignalNames));
    return producedSignals.filter((s) => isNew.has(s.name));
  }, [producedSignals, existingSignalNames]);
  const [tplTitle, setTplTitle] = useState('');
  const [tplPicked, setTplPicked] = useState<Record<string, boolean>>({});
  const [tplState, setTplState] = useState<'idle' | 'saving' | 'done'>('idle');
  const [tplError, setTplError] = useState('');
  const [tplTarget, setTplTarget] = useState<'existing' | 'new'>('new');
  const [tplQuery, setTplQuery] = useState('');
  const [tplResults, setTplResults] = useState<Template[]>([]);
  const [tplSearching, setTplSearching] = useState(false);
  const [tplSelected, setTplSelected] = useState<Template | null>(null);
  const tplRequestId = useRef(0);
  useEffect(() => {
    if (tplTarget !== 'existing') return;
    const thisRequest = ++tplRequestId.current;
    const timer = setTimeout(async () => {
      setTplSearching(true);
      try {
        const page = await queryClient.query({
          queryKey: queryKeys.templateSearch('mine', tplQuery, 0),
          queryFn: () => searchTemplates('mine', tplQuery),
        });
        const hydrated = (await Promise.all(page.data.map((n) => fetchTemplate(n)))).filter((t): t is Template => t !== null);
        if (tplRequestId.current === thisRequest) setTplResults(hydrated);
      } catch {
        if (tplRequestId.current === thisRequest) setTplResults([]);
      } finally {
        if (tplRequestId.current === thisRequest) setTplSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tplTarget, tplQuery]);
  const chosenHostSignals = newHostSignals.filter((s) => tplPicked[s.name] !== false);

  const toggle = (id: string) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const needsPaths = picked.has('du') && parseList(duPaths).length === 0;
  const scriptsOk = scripts.every((x) => SCRIPT_NAME.test(x.name.trim()) && x.command.trim() !== '');
  const setScript = (i: number, patch: Partial<CustomScript>) => setScripts((l) => l.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const canCreate = scriptsOk && title.trim() !== '' && channelNickname !== '' && picked.size > 0 && !needsPaths && !creating;

  const create = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError('');
    try {
      // Any collected host metric that already matches a Signal in a
      // template you own gets that template attached now, so this channel
      // starts recognizing those readings from the very first run instead
      // of needing a manual "add template" step later.
      if (destChannel) {
        const missing = producedSignals
          .map((s) => templateForSignalName[s.name.toLowerCase()])
          .filter((nick): nick is string => !!nick && !(destChannel.templateIds ?? []).includes(nick));
        if (missing.length > 0) await updateChannel({ ...destChannel, templateIds: [...(destChannel.templateIds ?? []), ...new Set(missing)] });
      }
      const spec = buildSystemSpec({
        channel: channelNickname,
        // keep catalog order so the saved spec is stable
        collectors: SYSTEM_COLLECTORS.map((c) => c.id).filter((id) => picked.has(id)),
        intervalSec: Number(intervalSec),
        duPaths: parseList(duPaths),
        dfMounts: parseList(dfMounts),
        scripts,
      });
      const saved = await addMapping({
        id: uid(),
        nickname: resolveNickname(nicknameInput, '@host', uid().slice(0, 8)),
        title: title.trim(),
        description: `Host metrics -> #${channelNickname}`,
        isPublic: false,
        sourceType: 'system',
        spec,
      });
      setCreated(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create this integration.');
    } finally {
      setCreating(false);
    }
  };

  const saveHostSignals = async () => {
    if (chosenHostSignals.length === 0 || !destChannel) return;
    if (tplTarget === 'existing' && !tplSelected) return;
    setTplState('saving');
    setTplError('');
    try {
      let saved: Template;
      if (tplTarget === 'existing' && tplSelected) {
        const have = new Set(tplSelected.signals.map((s) => s.name.toLowerCase()));
        const additions = chosenHostSignals.filter((s) => !have.has(s.name.toLowerCase()));
        saved =
          additions.length === 0
            ? tplSelected
            : {
                ...tplSelected,
                signals: [
                  ...tplSelected.signals,
                  ...additions.map((s, i) => ({ id: uid(), name: s.name, colorHex: COLORS[i % COLORS.length], emoji: s.emoji ?? '⚡️' })),
                ],
              };
        if (additions.length > 0) await updateTemplate(saved);
      } else {
        const nickname = resolveNickname('', '@signals', uid().slice(0, 8));
        saved = buildSignalTemplateEntries(tplTitle.trim() || title.trim() || 'Host metrics', nickname, chosenHostSignals, uid, profile?.nickname ?? '');
        await addTemplate(saved);
      }
      if (!(destChannel.templateIds ?? []).includes(saved.nickname)) {
        await updateChannel({ ...destChannel, templateIds: [...(destChannel.templateIds ?? []), saved.nickname] });
      }
      setTplState('done');
    } catch (err) {
      setTplError(err instanceof Error ? err.message : 'Unable to save these signals.');
      setTplState('idle');
    }
  };

  if (created) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="Host metrics" backTo="/integrations/catalog" />
        <FormSection title="Run the agent on the machine to monitor">
          <p className="text-sm text-black/60 dark:text-white/60 m-0">
            The integration is saved. On that machine run{' '}
            <code className="font-mono">zagentc import --mapping {created.nickname}</code> once to try it (with a daemon running it is handed to the daemon, no password needed), or keep{' '}
            <code className="font-mono">zagentc daemon --import</code> running to collect every {Number(intervalSec) || 60}s. Data appears here once the
            agent has run.
          </p>
          <p className="text-xs text-black/45 dark:text-white/45 m-0">
            To pin it to one machine when several run agents, set <code className="font-mono">"agent": "&lt;name&gt;"</code> in the spec (name as shown on
            the Agents page).
          </p>
          <p className="text-xs text-black/45 dark:text-white/45 m-0">
            Host readings go through the same mapping as any CSV or JSON source: the integration page's mapping editor lets you select fields, group by (e.g. by
            mount), aggregate and sort them before they become Signals.
          </p>
          <Button variant="secondary" onClick={() => navigate(`/integrations/${encodeURIComponent(created.id)}`)}>
            Open integration and shape the readings
          </Button>
        </FormSection>
        {newHostSignals.length > 0 && (
          <FormSection title="Signal schema">
            <p className="text-xs text-black/50 dark:text-white/50 m-0">
              A fresh Host metrics integration has no mapping rules, so readings arrive under their own fixed names (e.g. <code className="font-mono">cpu_usage_percent</code>) -
              they won't show up styled in #{channelNickname} until something maps them. Add them to an existing template or start a new one - either way
              it's attached to #{channelNickname} - or manage this later from the channel's Templates instead.
            </p>
            <div className="flex flex-wrap gap-2">
              {newHostSignals.map((s) => (
                <label key={s.name} className="flex items-center gap-1 text-[12px]">
                  <input type="checkbox" checked={tplPicked[s.name] !== false} onChange={(e) => setTplPicked((p) => ({ ...p, [s.name]: e.target.checked }))} />
                  {s.emoji} {s.name}
                </label>
              ))}
            </div>

            <SegmentedControl
              options={[
                { value: 'existing' as const, label: 'Add to a schema' },
                { value: 'new' as const, label: 'New schema' },
              ]}
              value={tplTarget}
              onChange={setTplTarget}
            />

            {tplTarget === 'existing' ? (
              <div className="flex flex-col gap-2">
                {tplSelected && (
                  <span className="text-xs text-black/50 dark:text-white/50">
                    Selected: <span className="font-medium text-black/70 dark:text-white/70">{tplSelected.title || tplSelected.nickname}</span>
                  </span>
                )}
                <TextField placeholder="Search my schemas" value={tplQuery} onChange={(e) => setTplQuery(e.target.value)} />
                <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
                  {tplSearching && <span className="text-sm text-black/40 dark:text-white/40 py-2 text-center">Searching…</span>}
                  {!tplSearching && tplResults.length === 0 && (
                    <span className="text-sm text-black/40 dark:text-white/40 py-2 text-center">
                      {tplQuery ? 'No schemas match that search.' : 'No schemas yet - create one instead.'}
                    </span>
                  )}
                  {!tplSearching &&
                    tplResults.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTplSelected(t)}
                        className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left ${
                          tplSelected?.id === t.id
                            ? 'border-blue-500 bg-blue-500/5'
                            : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'
                        }`}
                      >
                        <span className="w-8 h-8 rounded-full flex items-center justify-center text-base shrink-0" style={{ background: `${t.colorHex}1f` }}>
                          {t.emoji}
                        </span>
                        <span className="flex-1 min-w-0 truncate text-sm font-medium">{t.title || t.nickname}</span>
                        {tplSelected?.id === t.id && <Check size={16} className="text-blue-600 dark:text-blue-400 shrink-0" />}
                      </button>
                    ))}
                </div>
              </div>
            ) : (
              <TextField label="Schema name" value={tplTitle} onChange={(e) => setTplTitle(e.target.value)} placeholder={title || 'Host metrics'} />
            )}

            {tplError && <p role="alert" className="text-xs text-red-500 m-0">{tplError}</p>}
            <Button
              variant="secondary"
              onClick={() => void saveHostSignals()}
              disabled={chosenHostSignals.length === 0 || tplState !== 'idle' || (tplTarget === 'existing' && !tplSelected)}
            >
              {tplState === 'done'
                ? 'Saved and added to the channel'
                : tplTarget === 'existing'
                  ? `Add ${chosenHostSignals.length} Signal(s) to ${tplSelected ? tplSelected.title || tplSelected.nickname : 'the selected template'}`
                  : `Create schema (${chosenHostSignals.length}) and add to #${channelNickname}`}
            </Button>
          </FormSection>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Host metrics" backTo="/integrations/catalog" />
      <p className="text-xs text-black/45 dark:text-white/45 m-0">
        The cue-cli agent reads this machine's own CPU, memory, disks, network and sensors - no login or URL needed. It reads /proc and /sys directly and only
        runs shell commands you add below as custom scripts.
      </p>

      <FormSection title="Name this integration">
        <TextField label="Name" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. web-01 host metrics" />
        <NicknameField value={nicknameInput} onChange={setNicknameInput} example="@infra" />
      </FormSection>

      <FormSection title="What to collect">
        <div className="flex flex-col gap-2">
          {SYSTEM_COLLECTORS.map((c) => (
            <label key={c.id} className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="mt-1" checked={picked.has(c.id)} onChange={() => toggle(c.id)} aria-label={c.label} />
              <span>
                {c.label}
                <span className="block text-xs text-black/45 dark:text-white/45">{c.hint}</span>
              </span>
            </label>
          ))}
        </div>
        {picked.has('du') && (
          <TextArea
            label="Directories to measure (one per line)"
            value={duPaths}
            onChange={(e) => setDuPaths(e.target.value)}
            placeholder={'/var/log\n/home'}
            className="font-mono text-[13px]"
            spellCheck={false}
          />
        )}
        {picked.has('df') && (
          <TextField
            label="Only these mount points (optional, comma separated)"
            value={dfMounts}
            onChange={(e) => setDfMounts(e.target.value)}
            placeholder="/, /data"
            spellCheck={false}
          />
        )}
      </FormSection>

      <FormSection title="Custom scripts (optional)">
        <p className="text-xs text-black/45 dark:text-white/45 m-0">
          Need something the builtin collectors don't read? Add a shell command; the agent runs it on its own machine each interval. Print one line per metric:{' '}
          <code className="font-mono">name value key=value</code> (or just a number, named after the script). Scripts only run when that machine's operator has
          set <code className="font-mono">ZAGENTC_ALLOW_SCRIPTS=1</code> for the agent - saving one here never runs anything by itself.
        </p>
        {scripts.map((x, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-xl border border-black/10 dark:border-white/10 p-3">
            <div className="grid gap-2 sm:grid-cols-[1fr_7rem]">
              <TextField
                label="Script name"
                value={x.name}
                onChange={(e) => setScript(i, { name: e.target.value })}
                placeholder="queue_depth"
                error={x.name && !SCRIPT_NAME.test(x.name.trim()) ? 'Letters, digits, _ : . only' : undefined}
                spellCheck={false}
              />
              <TextField label="Timeout (s)" type="number" min={1} max={120} value={x.timeoutSec ?? 10} onChange={(e) => setScript(i, { timeoutSec: Number(e.target.value) })} />
            </div>
            <TextArea
              label="Command"
              value={x.command}
              onChange={(e) => setScript(i, { command: e.target.value })}
              placeholder={'wc -l < /var/spool/queue\n# or: echo "queue_depth $(ls /q | wc -l) queue=main"'}
              className="font-mono text-[13px]"
              spellCheck={false}
            />
            <Button variant="bordered" onClick={() => setScripts((l) => l.filter((_, k) => k !== i))}>
              Remove script
            </Button>
          </div>
        ))}
        {scripts.length < 16 && (
          <Button variant="secondary" onClick={() => setScripts((l) => [...l, { name: '', command: '', timeoutSec: 10 }])}>
            Add a custom script
          </Button>
        )}
      </FormSection>

      <FormSection title="How often, and where">
        <TextField label="Every (sec)" type="number" min={10} value={intervalSec} onChange={(e) => setIntervalSec(e.target.value)} />
        {ownedChannels.length === 0 ? (
          <p className="text-sm text-black/50 dark:text-white/50 m-0">Create a channel you own first - the metrics need somewhere to go.</p>
        ) : (
          <Select label="Channel" value={channelNickname} onChange={(e) => setChannelNickname(e.target.value)}>
            {ownedChannels.map((c) => (
              <option key={c.id} value={c.nickname}>
                {c.title || c.nickname}
              </option>
            ))}
          </Select>
        )}
      </FormSection>

      {error && <p role="alert" className="text-sm text-red-500 m-0">{error}</p>}
      <Button onClick={() => void create()} disabled={!canCreate}>
        {creating ? 'Creating…' : 'Create integration'}
      </Button>
    </div>
  );
}
